import { Zap, Droplets, Unlock, Activity, MonitorSmartphone, Check } from "lucide-react";
import { Reveal, SectionHead } from "@/components/Reveal";
import { IMAGES } from "@/content";

function Card({ className = "", children, testid }) {
  return (
    <div
      data-testid={testid}
      className={`card-hover relative overflow-hidden rounded-3xl border border-slate-200 bg-white p-7 sm:p-8 h-full ${className}`}
    >
      {children}
    </div>
  );
}

function IconBadge({ icon: Icon }) {
  return (
    <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-brand-soft text-brand-orange mb-5">
      <Icon className="h-5 w-5" />
    </span>
  );
}

export default function Bento() {
  return (
    <section id="beneficios" data-testid="benefits-section" className="relative py-20 lg:py-28 bg-white">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHead
          eyebrow="Por qué el EH8300 VET"
          title="Ingeniería que trabaja a favor de tu clínica"
          sub="Un analizador de hematología automático pensado para operar todos los días con menos costo, menos ataduras y resultados en los que puedes confiar."
        />

        <div className="mt-14 grid grid-cols-1 md:grid-cols-6 lg:grid-cols-12 gap-6 lg:gap-7">
          <Reveal className="md:col-span-3 lg:col-span-5" delay={0}>
            <Card testid="bento-reactivos-card" className="bg-brand-ink border-brand-ink text-white">
              <IconBadge icon={Droplets} />
              <div className="flex items-end gap-3">
                <span className="font-display text-7xl sm:text-8xl font-extrabold leading-none text-brand-orange">2</span>
                <span className="font-display text-xl font-bold pb-2">reactivos. Ni uno más.</span>
              </div>
              <p className="mt-4 text-slate-300 leading-relaxed">
                Solo diluyente y lisante: menos referencias que comprar, menos inventario que controlar y una operación
                más simple para tu equipo de trabajo.
              </p>
              <ul className="mt-5 space-y-2">
                {["Menor costo por prueba", "Fácil de abastecer", "Operación sencilla"].map((li) => (
                  <li key={li} className="flex items-center gap-2 text-sm font-semibold text-slate-200">
                    <Check className="h-4 w-4 text-brand-orange" /> {li}
                  </li>
                ))}
              </ul>
            </Card>
          </Reveal>

          <Reveal className="md:col-span-3 lg:col-span-7" delay={0.08}>
            <Card testid="bento-photo-card" className="p-0">
              <img
                src={IMAGES.vetSample}
                alt="Veterinaria tomando una micromuestra de sangre a un paciente canino"
                className="absolute inset-0 h-full w-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/85 via-brand-ink/20 to-transparent" />
              <div className="relative h-full min-h-[280px] flex flex-col justify-end p-7 sm:p-8">
                <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-brand-orange mb-2">
                  Micromuestra
                </span>
                <p className="font-display text-2xl font-bold text-white leading-snug max-w-sm">
                  Resultados completos con una gota de sangre.
                </p>
              </div>
            </Card>
          </Reveal>

          <Reveal className="md:col-span-2 lg:col-span-4" delay={0.05}>
            <Card testid="bento-velocidad-card">
              <IconBadge icon={Zap} />
              <div className="flex items-end gap-2">
                <span className="font-display text-6xl sm:text-7xl font-extrabold leading-none text-brand-ink">60</span>
              </div>
              <h3 className="font-display text-lg font-bold mt-2 text-brand-ink">muestras por hora</h3>
              <p className="mt-3 text-slate-600 leading-relaxed">
                Respuestas en minutos: el ritmo que tu clínica necesita en las horas de mayor consulta.
              </p>
            </Card>
          </Reveal>

          <Reveal className="md:col-span-4 lg:col-span-4" delay={0.13}>
            <Card testid="bento-sistema-abierto-card">
              <IconBadge icon={Unlock} />
              <h3 className="font-display text-xl font-bold text-brand-ink">Reactivo abierto, sin código de barras</h3>
              <p className="mt-3 text-slate-600 leading-relaxed">
                No necesita tarjetas ni códigos para funcionar. Tú decides a quién le compras tus reactivos, sin
                depender de un solo proveedor.
              </p>
              <div className="mt-5 flex flex-wrap gap-2">
                {["Sin bloqueos", "Libertad de proveedor", "Tu equipo, tus reglas"].map((t) => (
                  <span
                    key={t}
                    className="rounded-full border border-slate-200 bg-slate-50 px-3.5 py-1.5 text-xs font-bold text-slate-600"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </Card>
          </Reveal>

          <Reveal className="md:col-span-6 lg:col-span-4" delay={0.21}>
            <Card testid="bento-pantalla-card">
              <IconBadge icon={MonitorSmartphone} />
              <h3 className="font-display text-xl font-bold text-brand-ink">Pantalla táctil LED de 10″ a color</h3>
              <p className="mt-3 text-slate-600 leading-relaxed">
                Fácil de usar y en español, con opción de mouse y teclado externos para tu equipo.
              </p>
            </Card>
          </Reveal>

          <Reveal className="md:col-span-6 lg:col-span-12" delay={0.1}>
            <Card testid="bento-parametros-card" className="bg-brand-cloud">
              <div className="grid lg:grid-cols-[1fr_1.2fr] gap-8 items-center">
                <div>
                  <IconBadge icon={Activity} />
                  <div className="flex items-end gap-3 flex-wrap">
                    <span className="font-display text-5xl sm:text-6xl font-extrabold leading-none text-brand-ink">
                      24
                    </span>
                    <span className="font-display text-lg font-bold pb-1.5 text-brand-ink">
                      parámetros + 3 histogramas
                    </span>
                  </div>
                  <p className="mt-4 text-slate-600 leading-relaxed max-w-md">
                    Hemograma completo con diferencial leucocitario de 3 partes (LYM, MID, GRA) e histogramas de WBC,
                    RBC y PLT en cada corrida.
                  </p>
                </div>
                <div className="flex flex-wrap gap-2" data-testid="bento-parameter-chips">
                  {["WBC", "RBC", "HGB", "HCT", "MCV", "MCH", "MCHC", "PLT", "LYM%", "MID%", "GRA%", "RDW-CV", "MPV", "P-LCR"].map(
                    (p) => (
                      <span
                        key={p}
                        className="font-mono2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm"
                      >
                        {p}
                      </span>
                    )
                  )}
                </div>
              </div>
            </Card>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
